import { useState, useEffect } from 'react'
import { Loader2, BarChart3, TrendingUp, Users, ChevronDown, ChevronRight, ArrowLeft, Sparkles, BrainCircuit } from 'lucide-react'
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, Legend } from 'recharts'
import { rfpApi } from '@/api/client'
import { showToast } from '@/store/uiStore'

interface ProjectStats {
  id: number
  title: string
  created_at: string
  total: number
  auto_accepted: number
  approved: number
  flagged: number
  accepted_unchanged: number
  accepted_edited: number
  has_brain: number
  brain_chosen: number
  brain_enabled: number
  source_pass1: number
  source_pass1_plus_kb: number
  source_kb_only: number
  source_manual: number
}

interface ReviewerStats {
  name: string
  total: number
  accepted: number
  flagged: number
  accepted_unchanged: number
  accepted_edited: number
  projects_count: number
}

interface EditedSample {
  id: number
  requirement_text: string
  ai_response: string
  brain_response: string | null
  final_response: string
  source: string
  confidence: string | null
  assigned_to_name: string | null
  assigned_category: string | null
  project_title: string
}

interface EditAnalysis {
  id: number
  edit_type: string
  description: string
  source_chosen: string
}

interface AnalysisSummary {
  top_patterns: Array<{ type: string; count: number; description: string }>
  prompt_recommendations: string[]
  accuracy_notes: string
}

interface RfpAiPerformanceProps {
  onBack: () => void
}

function pct(n: number, d: number): string {
  if (d === 0) return '-'
  return Math.round((n / d) * 100) + '%'
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: '2-digit' })
}

function sourceLabel(source: string, confidence: string | null): string {
  if (source === 'reference') return 'Pass 1 (Samples)'
  if (source === 'cassidy' && confidence === 'high') return 'Samples + KB'
  if (source === 'cassidy') return 'KB Only'
  if (source === 'manual') return 'Manual'
  return source
}

const EDIT_TYPE_CONFIG: Record<string, { label: string; color: string }> = {
  added_specificity: { label: 'Added Specificity', color: 'text-blue-400' },
  corrected_inaccuracy: { label: 'Corrected Inaccuracy', color: 'text-red-400' },
  added_customer_context: { label: 'Added Customer Context', color: 'text-indigo-400' },
  changed_tone: { label: 'Changed Tone', color: 'text-purple-400' },
  restructured: { label: 'Restructured', color: 'text-cyan-400' },
  condensed: { label: 'Condensed', color: 'text-teal-400' },
  expanded: { label: 'Expanded', color: 'text-amber-400' },
  replaced_entirely: { label: 'Replaced Entirely', color: 'text-red-400' },
  minor_wording: { label: 'Minor Wording', color: 'text-emerald-400' },
}

export function RfpAiPerformance({ onBack }: RfpAiPerformanceProps) {
  const [loading, setLoading] = useState(true)
  const [projects, setProjects] = useState<ProjectStats[]>([])
  const [reviewers, setReviewers] = useState<ReviewerStats[]>([])
  const [editedSamples, setEditedSamples] = useState<EditedSample[]>([])
  const [expandedSample, setExpandedSample] = useState<number | null>(null)
  const [analyzing, setAnalyzing] = useState(false)
  const [editAnalysis, setEditAnalysis] = useState<EditAnalysis[]>([])
  const [analysisSummary, setAnalysisSummary] = useState<AnalysisSummary | null>(null)

  useEffect(() => {
    (async () => {
      const res = await rfpApi.getAiPerformance()
      if (res.success && res.data) {
        const d = (res.data as any)?.data ?? res.data
        setProjects(d.projects || [])
        setReviewers(d.reviewers || [])
        setEditedSamples(d.edited_samples || [])
      }
      setLoading(false)
    })()
  }, [])

  const runAnalysis = async () => {
    setAnalyzing(true)
    try {
      const res = await rfpApi.analyzeEdits()
      if (res.success && res.data) {
        const d = (res.data as any)?.data ?? res.data
        setEditAnalysis(d.edits || [])
        setAnalysisSummary(d.summary || null)
        showToast('Analysis complete', 'success')
      } else {
        showToast((res as any).error || 'Analysis failed', 'error')
      }
    } catch {
      showToast('Analysis failed', 'error')
    }
    setAnalyzing(false)
  }

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 size={20} className="animate-spin text-[var(--text-muted)]" />
      </div>
    )
  }

  // Aggregate totals
  const totals = projects.reduce((acc, p) => ({
    total: acc.total + p.total,
    auto_accepted: acc.auto_accepted + p.auto_accepted,
    approved: acc.approved + p.approved,
    flagged: acc.flagged + p.flagged,
    accepted_unchanged: acc.accepted_unchanged + p.accepted_unchanged,
    accepted_edited: acc.accepted_edited + p.accepted_edited,
    has_brain: acc.has_brain + p.has_brain,
    brain_chosen: acc.brain_chosen + p.brain_chosen,
    source_pass1: acc.source_pass1 + p.source_pass1,
    source_pass1_plus_kb: acc.source_pass1_plus_kb + p.source_pass1_plus_kb,
    source_kb_only: acc.source_kb_only + p.source_kb_only,
    source_manual: acc.source_manual + p.source_manual,
  }), { total: 0, auto_accepted: 0, approved: 0, flagged: 0, accepted_unchanged: 0, accepted_edited: 0, has_brain: 0, brain_chosen: 0, source_pass1: 0, source_pass1_plus_kb: 0, source_kb_only: 0, source_manual: 0 })

  const totalResolved = totals.auto_accepted + totals.approved + totals.flagged
  const totalAccepted = totals.auto_accepted + totals.approved
  const aiAccuracyRate = totalResolved > 0 ? ((totals.auto_accepted + totals.accepted_unchanged) / totalResolved) * 100 : 0

  // Build analysis lookup
  const analysisMap = new Map<number, EditAnalysis>()
  for (const a of editAnalysis) analysisMap.set(a.id, a)

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          onClick={onBack}
          className="p-1.5 rounded-lg text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-secondary)] transition-colors"
        >
          <ArrowLeft size={18} />
        </button>
        <div>
          <h2 className="text-lg font-semibold text-[var(--text-primary)]">AI Performance Report</h2>
          <p className="text-xs text-[var(--text-muted)] mt-0.5">
            How well is the AI performing across all RFP projects
          </p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {[
          { label: 'Total Questions', value: String(totals.total), color: 'text-[var(--text-primary)]' },
          { label: 'Auto-Accepted', value: String(totals.auto_accepted), sub: pct(totals.auto_accepted, totals.total), color: 'text-emerald-400' },
          { label: 'AI Accuracy', value: Math.round(aiAccuracyRate) + '%', sub: 'auto + unchanged', color: 'text-blue-400' },
          { label: 'Human Edited', value: String(totals.accepted_edited), sub: pct(totals.accepted_edited, totalAccepted), color: 'text-amber-400' },
          { label: 'Brain Chosen', value: String(totals.brain_chosen), sub: totals.has_brain > 0 ? pct(totals.brain_chosen, totals.has_brain) + ' of brain' : '-', color: 'text-pink-400' },
        ].map(s => (
          <div key={s.label} className="rounded-xl bg-[var(--bg-secondary)] border border-[var(--border-color)] px-4 py-4 text-center">
            <div className={`text-2xl font-bold tabular-nums ${s.color}`}>{s.value}</div>
            <div className="text-[10px] text-[var(--text-muted)] uppercase tracking-wider mt-1">{s.label}</div>
            {s.sub && <div className="text-[10px] text-[var(--text-muted)] mt-0.5">{s.sub}</div>}
          </div>
        ))}
      </div>

      {/* Trend Chart */}
      {projects.filter(p => p.auto_accepted + p.approved + p.flagged > 0).length >= 2 && (() => {
        const chartData = projects
          .filter(p => p.auto_accepted + p.approved + p.flagged > 0)
          .map(p => {
            const resolved = p.auto_accepted + p.approved + p.flagged
            const autoAcceptRate = Math.round((p.auto_accepted / p.total) * 100)
            const aiAccuracy = Math.round(((p.auto_accepted + p.accepted_unchanged) / resolved) * 100)
            const editRate = p.approved > 0 ? Math.round((p.accepted_edited / p.approved) * 100) : null
            return {
              name: p.title.length > 15 ? p.title.slice(0, 15) + '...' : p.title,
              fullName: p.title,
              autoAcceptRate,
              aiAccuracy,
              editRate,
            }
          })

        return (
          <div className="rounded-xl border border-[var(--border-color)] bg-[var(--bg-secondary)] overflow-hidden">
            <div className="flex items-center gap-2 px-5 py-3.5 border-b border-[var(--border-color)]">
              <TrendingUp size={15} className="text-emerald-400" />
              <span className="text-sm font-semibold text-[var(--text-primary)]">Performance Trend</span>
              <span className="text-[10px] text-[var(--text-muted)] ml-1">(is AI getting better over time?)</span>
            </div>
            <div className="p-5">
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={chartData} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" />
                  <XAxis
                    dataKey="name"
                    tick={{ fontSize: 10, fill: 'var(--text-muted)' }}
                    axisLine={{ stroke: 'var(--border-color)' }}
                    tickLine={{ stroke: 'var(--border-color)' }}
                  />
                  <YAxis
                    domain={[0, 100]}
                    tick={{ fontSize: 10, fill: 'var(--text-muted)' }}
                    axisLine={{ stroke: 'var(--border-color)' }}
                    tickLine={{ stroke: 'var(--border-color)' }}
                    tickFormatter={(v: number) => `${v}%`}
                  />
                  <RechartsTooltip
                    contentStyle={{
                      backgroundColor: 'var(--bg-secondary)',
                      border: '1px solid var(--border-color)',
                      borderRadius: '8px',
                      fontSize: '11px',
                    }}
                    labelStyle={{ color: 'var(--text-primary)', fontWeight: 600, marginBottom: 4 }}
                    labelFormatter={(_: any, payload: readonly any[]) => payload?.[0]?.payload?.fullName || String(_)}
                    formatter={(value: any, name: any) => [`${value}%`, String(name)]}
                  />
                  <Legend
                    wrapperStyle={{ fontSize: '11px' }}
                    iconType="circle"
                    iconSize={8}
                  />
                  <Line
                    type="monotone"
                    dataKey="autoAcceptRate"
                    name="Auto-Accept Rate"
                    stroke="#34d399"
                    strokeWidth={2.5}
                    dot={{ r: 4, fill: '#34d399' }}
                    activeDot={{ r: 6 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="aiAccuracy"
                    name="AI Accuracy (auto + unchanged)"
                    stroke="#60a5fa"
                    strokeWidth={2.5}
                    dot={{ r: 4, fill: '#60a5fa' }}
                    activeDot={{ r: 6 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="editRate"
                    name="Edit Rate (lower is better)"
                    stroke="#fbbf24"
                    strokeWidth={2}
                    strokeDasharray="5 5"
                    dot={{ r: 3, fill: '#fbbf24' }}
                    activeDot={{ r: 5 }}
                    connectNulls
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )
      })()}

      {/* Source Attribution */}
      <div className="rounded-xl border border-[var(--border-color)] bg-[var(--bg-secondary)] overflow-hidden">
        <div className="flex items-center gap-2 px-5 py-3.5 border-b border-[var(--border-color)]">
          <Sparkles size={15} className="text-violet-400" />
          <span className="text-sm font-semibold text-[var(--text-primary)]">Response Sources</span>
          <span className="text-[10px] text-[var(--text-muted)] ml-1">(which AI pass produced the final answer)</span>
        </div>
        <div className="p-5">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { label: 'Pass 1 (Samples)', value: totals.source_pass1, pctVal: pct(totals.source_pass1, totals.total), color: 'text-violet-400', bg: 'bg-violet-500' },
              { label: 'Samples + KB', value: totals.source_pass1_plus_kb, pctVal: pct(totals.source_pass1_plus_kb, totals.total), color: 'text-blue-400', bg: 'bg-blue-500' },
              { label: 'KB Only', value: totals.source_kb_only, pctVal: pct(totals.source_kb_only, totals.total), color: 'text-cyan-400', bg: 'bg-cyan-500' },
              { label: 'Manual', value: totals.source_manual, pctVal: pct(totals.source_manual, totals.total), color: 'text-[var(--text-muted)]', bg: 'bg-[var(--text-muted)]' },
            ].map(s => (
              <div key={s.label} className="text-center">
                <div className={`text-lg font-bold tabular-nums ${s.color}`}>{s.value}</div>
                <div className="text-[10px] text-[var(--text-muted)] uppercase tracking-wider">{s.label}</div>
                <div className="text-[10px] text-[var(--text-muted)]">{s.pctVal}</div>
              </div>
            ))}
          </div>
          {/* Visual bar */}
          {totals.total > 0 && (
            <div className="flex h-3 rounded-full overflow-hidden mt-4 bg-[var(--bg-primary)]">
              {totals.source_pass1 > 0 && <div className="bg-violet-500 transition-all" style={{ width: `${(totals.source_pass1 / totals.total) * 100}%` }} />}
              {totals.source_pass1_plus_kb > 0 && <div className="bg-blue-500 transition-all" style={{ width: `${(totals.source_pass1_plus_kb / totals.total) * 100}%` }} />}
              {totals.source_kb_only > 0 && <div className="bg-cyan-500 transition-all" style={{ width: `${(totals.source_kb_only / totals.total) * 100}%` }} />}
              {totals.source_manual > 0 && <div className="bg-gray-500 transition-all" style={{ width: `${(totals.source_manual / totals.total) * 100}%` }} />}
            </div>
          )}
        </div>
      </div>

      {/* Per-Project Trend Table */}
      <div className="rounded-xl border border-[var(--border-color)] bg-[var(--bg-secondary)] overflow-hidden">
        <div className="flex items-center gap-2 px-5 py-3.5 border-b border-[var(--border-color)]">
          <TrendingUp size={15} className="text-indigo-400" />
          <span className="text-sm font-semibold text-[var(--text-primary)]">Performance by Project</span>
          <span className="text-[10px] text-[var(--text-muted)] ml-1">(chronological)</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-[var(--bg-primary)] text-[var(--text-muted)]">
                <th className="text-left px-4 py-2.5 font-medium">Project</th>
                <th className="text-center px-3 py-2.5 font-medium">Date</th>
                <th className="text-center px-3 py-2.5 font-medium">Total</th>
                <th className="text-center px-3 py-2.5 font-medium">Auto-Accepted</th>
                <th className="text-center px-3 py-2.5 font-medium">Unchanged</th>
                <th className="text-center px-3 py-2.5 font-medium">Edited</th>
                <th className="text-center px-3 py-2.5 font-medium">Flagged</th>
                <th className="text-center px-3 py-2.5 font-medium">
                  <span className="flex items-center justify-center gap-1"><BrainCircuit size={10} className="text-pink-400" /> Brain</span>
                </th>
                <th className="text-center px-3 py-2.5 font-medium">Source</th>
                <th className="text-center px-3 py-2.5 font-medium">AI Accuracy</th>
              </tr>
            </thead>
            <tbody>
              {projects.map(p => {
                const resolved = p.auto_accepted + p.approved + p.flagged
                const accuracy = resolved > 0 ? Math.round(((p.auto_accepted + p.accepted_unchanged) / resolved) * 100) : null
                return (
                  <tr key={p.id} className="border-t border-[var(--border-color)] hover:bg-[var(--bg-tertiary)] transition-colors">
                    <td className="px-4 py-2.5 text-[var(--text-primary)] font-medium whitespace-nowrap">{p.title}</td>
                    <td className="px-3 py-2.5 text-center text-[var(--text-muted)] whitespace-nowrap">{formatDate(p.created_at)}</td>
                    <td className="px-3 py-2.5 text-center text-[var(--text-primary)] tabular-nums">{p.total}</td>
                    <td className="px-3 py-2.5 text-center tabular-nums">
                      <span className="text-emerald-400 font-medium">{p.auto_accepted}</span>
                      <span className="text-[var(--text-muted)] ml-1">{pct(p.auto_accepted, p.total)}</span>
                    </td>
                    <td className="px-3 py-2.5 text-center tabular-nums">
                      <span className="text-blue-400 font-medium">{p.accepted_unchanged}</span>
                      {p.approved > 0 && <span className="text-[var(--text-muted)] ml-1">{pct(p.accepted_unchanged, p.approved)}</span>}
                    </td>
                    <td className="px-3 py-2.5 text-center tabular-nums">
                      <span className="text-amber-400 font-medium">{p.accepted_edited}</span>
                      {p.approved > 0 && <span className="text-[var(--text-muted)] ml-1">{pct(p.accepted_edited, p.approved)}</span>}
                    </td>
                    <td className="px-3 py-2.5 text-center tabular-nums">
                      {p.flagged > 0 ? <span className="text-red-400 font-medium">{p.flagged}</span> : <span className="text-[var(--text-muted)]">0</span>}
                    </td>
                    <td className="px-3 py-2.5 text-center tabular-nums whitespace-nowrap">
                      {p.has_brain > 0 ? (
                        <span>
                          <span className="text-pink-400 font-medium">{p.brain_chosen}</span>
                          <span className="text-[var(--text-muted)]">/{p.has_brain}</span>
                        </span>
                      ) : (
                        <span className="text-[var(--text-muted)]">-</span>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-center whitespace-nowrap">
                      <div className="flex items-center justify-center gap-1">
                        {p.source_pass1 > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded bg-violet-500/10 text-violet-400">{p.source_pass1} P1</span>}
                        {p.source_pass1_plus_kb > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-500/10 text-blue-400">{p.source_pass1_plus_kb} P1+KB</span>}
                        {p.source_kb_only > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded bg-cyan-500/10 text-cyan-400">{p.source_kb_only} KB</span>}
                      </div>
                    </td>
                    <td className="px-3 py-2.5 text-center">
                      {accuracy !== null ? (
                        <span className={`font-bold tabular-nums ${accuracy >= 80 ? 'text-emerald-400' : accuracy >= 60 ? 'text-amber-400' : 'text-red-400'}`}>
                          {accuracy}%
                        </span>
                      ) : (
                        <span className="text-[var(--text-muted)]">-</span>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Per-Reviewer Stats */}
      {reviewers.length > 0 && (
        <div className="rounded-xl border border-[var(--border-color)] bg-[var(--bg-secondary)] overflow-hidden">
          <div className="flex items-center gap-2 px-5 py-3.5 border-b border-[var(--border-color)]">
            <Users size={15} className="text-cyan-400" />
            <span className="text-sm font-semibold text-[var(--text-primary)]">By Reviewer</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-[var(--bg-primary)] text-[var(--text-muted)]">
                  <th className="text-left px-4 py-2.5 font-medium">Reviewer</th>
                  <th className="text-center px-3 py-2.5 font-medium">Projects</th>
                  <th className="text-center px-3 py-2.5 font-medium">Assigned</th>
                  <th className="text-center px-3 py-2.5 font-medium">Accepted</th>
                  <th className="text-center px-3 py-2.5 font-medium">Unchanged</th>
                  <th className="text-center px-3 py-2.5 font-medium">Edited</th>
                  <th className="text-center px-3 py-2.5 font-medium">Flagged</th>
                  <th className="text-center px-3 py-2.5 font-medium">Edit Rate</th>
                </tr>
              </thead>
              <tbody>
                {reviewers.map(r => {
                  const editRate = r.accepted > 0 ? Math.round((r.accepted_edited / r.accepted) * 100) : null
                  return (
                    <tr key={r.name} className="border-t border-[var(--border-color)] hover:bg-[var(--bg-tertiary)] transition-colors">
                      <td className="px-4 py-2.5 text-[var(--text-primary)] font-medium">{r.name}</td>
                      <td className="px-3 py-2.5 text-center text-[var(--text-muted)] tabular-nums">{r.projects_count}</td>
                      <td className="px-3 py-2.5 text-center text-[var(--text-primary)] tabular-nums">{r.total}</td>
                      <td className="px-3 py-2.5 text-center text-emerald-400 tabular-nums font-medium">{r.accepted}</td>
                      <td className="px-3 py-2.5 text-center text-blue-400 tabular-nums">{r.accepted_unchanged}</td>
                      <td className="px-3 py-2.5 text-center text-amber-400 tabular-nums">{r.accepted_edited}</td>
                      <td className="px-3 py-2.5 text-center tabular-nums">
                        {r.flagged > 0 ? <span className="text-red-400">{r.flagged}</span> : <span className="text-[var(--text-muted)]">0</span>}
                      </td>
                      <td className="px-3 py-2.5 text-center">
                        {editRate !== null ? (
                          <span className={`font-medium tabular-nums ${editRate <= 20 ? 'text-emerald-400' : editRate <= 50 ? 'text-amber-400' : 'text-red-400'}`}>
                            {editRate}%
                          </span>
                        ) : (
                          <span className="text-[var(--text-muted)]">-</span>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* AI Analysis of Edits */}
      {editedSamples.length > 0 && (
        <div className="rounded-xl border border-[var(--border-color)] bg-[var(--bg-secondary)] overflow-hidden">
          <div className="flex items-center justify-between px-5 py-3.5 border-b border-[var(--border-color)]">
            <div className="flex items-center gap-2">
              <BarChart3 size={15} className="text-amber-400" />
              <span className="text-sm font-semibold text-[var(--text-primary)]">Edited Responses</span>
              <span className="text-[10px] text-[var(--text-muted)]">({editedSamples.length} most recent)</span>
            </div>
            <button
              onClick={runAnalysis}
              disabled={analyzing}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-indigo-600 text-white hover:bg-indigo-500 transition-colors disabled:opacity-50"
            >
              {analyzing ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
              {analyzing ? 'Analyzing...' : editAnalysis.length > 0 ? 'Re-analyze' : 'Analyze with AI'}
            </button>
          </div>

          {/* AI Analysis Summary */}
          {analysisSummary && (
            <div className="px-5 py-4 border-b border-[var(--border-color)] space-y-4">
              {/* Top patterns */}
              <div>
                <h4 className="text-[11px] font-semibold text-[var(--text-muted)] uppercase tracking-wider mb-2">Top Edit Patterns</h4>
                <div className="flex flex-wrap gap-2">
                  {analysisSummary.top_patterns.map((p, i) => {
                    const cfg = EDIT_TYPE_CONFIG[p.type] || { label: p.type, color: 'text-[var(--text-secondary)]' }
                    return (
                      <div key={i} className="flex items-center gap-2 rounded-lg bg-[var(--bg-primary)] border border-[var(--border-color)] px-3 py-2">
                        <span className={`text-sm font-bold tabular-nums ${cfg.color}`}>{p.count}</span>
                        <div>
                          <span className={`text-xs font-medium ${cfg.color}`}>{cfg.label}</span>
                          <p className="text-[10px] text-[var(--text-muted)]">{p.description}</p>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>

              {/* Prompt recommendations */}
              {analysisSummary.prompt_recommendations.length > 0 && (
                <div>
                  <h4 className="text-[11px] font-semibold text-[var(--text-muted)] uppercase tracking-wider mb-2">Prompt Improvement Recommendations</h4>
                  <div className="rounded-lg bg-indigo-500/5 border border-indigo-500/15 px-4 py-3 space-y-2">
                    {analysisSummary.prompt_recommendations.map((r, i) => (
                      <p key={i} className="text-xs text-[var(--text-secondary)] leading-relaxed">
                        <span className="text-indigo-400 font-medium mr-1">{i + 1}.</span> {r}
                      </p>
                    ))}
                  </div>
                </div>
              )}

              {/* Accuracy notes */}
              {analysisSummary.accuracy_notes && (
                <div>
                  <h4 className="text-[11px] font-semibold text-[var(--text-muted)] uppercase tracking-wider mb-2">Accuracy Notes</h4>
                  <p className="text-xs text-[var(--text-secondary)] leading-relaxed">{analysisSummary.accuracy_notes}</p>
                </div>
              )}
            </div>
          )}

          {/* Individual edited samples */}
          <div className="divide-y divide-[var(--border-color)]">
            {editedSamples.map(s => {
              const isExpanded = expandedSample === s.id
              const analysis = analysisMap.get(s.id)
              const editCfg = analysis ? (EDIT_TYPE_CONFIG[analysis.edit_type] || { label: analysis.edit_type, color: 'text-[var(--text-secondary)]' }) : null
              return (
                <div key={s.id}>
                  <button
                    onClick={() => setExpandedSample(isExpanded ? null : s.id)}
                    className="flex items-start gap-3 w-full px-5 py-3 text-left hover:bg-[var(--bg-tertiary)] transition-colors"
                  >
                    {isExpanded ? <ChevronDown size={13} className="text-[var(--text-muted)] mt-0.5 shrink-0" /> : <ChevronRight size={13} className="text-[var(--text-muted)] mt-0.5 shrink-0" />}
                    <div className="min-w-0 flex-1">
                      <p className="text-xs text-[var(--text-primary)] font-medium truncate">{s.requirement_text}</p>
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        <span className="text-[10px] text-indigo-400">{s.project_title}</span>
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-[var(--bg-primary)] text-[var(--text-muted)]">{sourceLabel(s.source, s.confidence)}</span>
                        {s.assigned_to_name && <span className="text-[10px] text-[var(--text-muted)]">{s.assigned_to_name}</span>}
                        {s.assigned_category && <span className="text-[10px] px-1.5 py-0.5 rounded bg-[var(--bg-primary)] text-[var(--text-muted)]">{s.assigned_category}</span>}
                        {editCfg && (
                          <span className={`text-[10px] px-1.5 py-0.5 rounded bg-[var(--bg-primary)] font-medium ${editCfg.color}`}>
                            {editCfg.label}
                          </span>
                        )}
                        {analysis?.source_chosen && (
                          <span className={`text-[10px] px-1.5 py-0.5 rounded ${analysis.source_chosen === 'brain' ? 'bg-pink-500/10 text-pink-400' : analysis.source_chosen === 'rewritten' ? 'bg-amber-500/10 text-amber-400' : 'bg-violet-500/10 text-violet-400'}`}>
                            {analysis.source_chosen === 'brain' ? 'Used Brain' : analysis.source_chosen === 'rewritten' ? 'Rewritten' : 'Used Cassidy'}
                          </span>
                        )}
                      </div>
                      {analysis?.description && (
                        <p className="text-[10px] text-[var(--text-muted)] mt-1 italic">{analysis.description}</p>
                      )}
                    </div>
                  </button>
                  {isExpanded && (
                    <div className="px-5 pb-4 space-y-3 ml-7">
                      <div className="rounded-lg bg-red-500/5 border border-red-500/15 px-4 py-3">
                        <p className="text-[10px] font-semibold text-red-400 uppercase tracking-wider mb-1.5">
                          AI Draft <span className="normal-case font-normal text-[var(--text-muted)]">({sourceLabel(s.source, s.confidence)})</span>
                        </p>
                        <p className="text-xs text-[var(--text-secondary)] leading-relaxed whitespace-pre-wrap">{s.ai_response}</p>
                      </div>
                      {s.brain_response && (
                        <div className="rounded-lg bg-pink-500/5 border border-pink-500/15 px-4 py-3">
                          <p className="text-[10px] font-semibold text-pink-400 uppercase tracking-wider mb-1.5">Brain Draft</p>
                          <p className="text-xs text-[var(--text-secondary)] leading-relaxed whitespace-pre-wrap">{s.brain_response}</p>
                        </div>
                      )}
                      <div className="rounded-lg bg-emerald-500/5 border border-emerald-500/15 px-4 py-3">
                        <p className="text-[10px] font-semibold text-emerald-400 uppercase tracking-wider mb-1.5">Final (Human Edited)</p>
                        <p className="text-xs text-[var(--text-secondary)] leading-relaxed whitespace-pre-wrap">{s.final_response}</p>
                      </div>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
